import React, { useState } from 'react';
import { Timeline } from 'primereact/timeline';
import CollapsableCard from 'src/common/refactoredComponents/collapsableCard/CollapsableCard.jsx';
import { useSelector } from 'react-redux';
import { convertTime24To12 } from 'src/utils/util';
import { getDisplayDate, getTimeZoneName } from 'src/utils/dateUtils';
import PoHistoryApprove from 'src/assets/images/PoHistoryApprove.svg';
import PoHistoryAwaiting from 'src/assets/images/PoHistoryAwaiting.svg';
import PoHistoryCancelled from 'src/assets/images/PoHistoryCancelled.svg';
import PoHistoryComplete from 'src/assets/images/PoHistoryComplete.svg';
import PoHistoryDenied from 'src/assets/images/PoHistoryDenied.svg';
import PoHistoryReassigned from 'src/assets/images/PoHistoryReassigned.svg';
import PoHistorySubmitted from 'src/assets/images/PoHistorySubmitted.svg';
import { PriceOverrideHistoryStatus } from 'src/constants/orderListConstants';
import { sizes } from 'src/styles/Media.styled';
import {
    DateContainer,
    TimeContainer,
    Status,
    Tag,
    Demo,
    Span,
    SpanStatus,
    Character,
    HistoryContent,
} from './PriceOverrideHistory.styled';
import { useTranslation } from 'react-i18next';

const PriceOverrideHistory = () => {
    const { t } = useTranslation();
    const { actionHistory } = useSelector(
        (state) => state.orderSummaryReducer.priceOverride
    );

    const winWidth = window.innerWidth;

    const { orderBasicDetails } = useSelector((state) => ({
        orderBasicDetails: state.orderSummaryReducer.orderBasicDetails,
    }));

    const [expand, setExpand] = useState(true);

    const selectColor = (status) => {
        switch (status) {
            case PriceOverrideHistoryStatus.dMReset:
            case PriceOverrideHistoryStatus.requestorReset:
            case PriceOverrideHistoryStatus.complete:
                return '#14853D';
            case PriceOverrideHistoryStatus.approved:
                return '#7DC929';
            case PriceOverrideHistoryStatus.reassigned:
            case PriceOverrideHistoryStatus.reassignedTo:
            case PriceOverrideHistoryStatus.reassignedBy:
                return '#654ADC';
            case PriceOverrideHistoryStatus.awaitingActionBy:
                return '#DC880A';
            case PriceOverrideHistoryStatus.resubmitted:
            case PriceOverrideHistoryStatus.submitted:
                return '#1010EB';
            case PriceOverrideHistoryStatus.denied:
                return '#EF7692';
            case PriceOverrideHistoryStatus.canceledBy:
                return '#ED002A';
            default:
                return;
        }
    };

    const selectIcon = (status) => {
        switch (status) {
            case PriceOverrideHistoryStatus.dMReset:
            case PriceOverrideHistoryStatus.requestorReset:
            case PriceOverrideHistoryStatus.complete:
                return PoHistoryComplete;
            case PriceOverrideHistoryStatus.approved:
                return PoHistoryApprove;
            case PriceOverrideHistoryStatus.reassigned:
            case PriceOverrideHistoryStatus.reassignedTo:
            case PriceOverrideHistoryStatus.reassignedBy:
                return PoHistoryReassigned;
            case PriceOverrideHistoryStatus.awaitingActionBy:
                return PoHistoryAwaiting;
            case PriceOverrideHistoryStatus.resubmitted:
            case PriceOverrideHistoryStatus.submitted:
                return PoHistorySubmitted;
            case PriceOverrideHistoryStatus.denied:
                return PoHistoryDenied;
            case PriceOverrideHistoryStatus.canceledBy:
                return PoHistoryCancelled;
            default:
                return;
        }
    };

    const eventsComplete = actionHistory?.map((event) => {
        return {
            status: event.status,
            date: event?.dateTime
                ? getDisplayDate(new Date(event.dateTime))
                : null,
            time: event?.dateTime
                ? convertTime24To12(new Date(event.dateTime))
                : null,
            timeZone: event?.dateTime ? getTimeZoneName() : null,
            color: selectColor(event.status),
            icon: selectIcon(event.status),
            tag:
                event?.level && event?.owner
                    ? `${event.level} ${event.owner}`
                    : event?.level
                    ? event.level
                    : event?.owner
                    ? event.owner
                    : null,
            description: event?.description ? event.description : null,
            levelDescritpion: event?.levelDescritpion
                ? event.levelDescritpion
                : null,
        };
    });

    const customizedMarker = (item) => {
        return (
            <Span
                className="custom-marker p-shadow-2"
                backgroundColor={item.color}
            >
                {/* <i className={item.icon}></i> */}
                <img src={item.icon} alt={''} />
            </Span>
        );
    };

    const customizedContent = (item) => {
        return (
            <HistoryContent>
                <SpanStatus>
                    <Status style={{ color: item.color }}>{item.status}</Status>
                    <Character></Character>
                    <Tag>{item.tag}</Tag>
                </SpanStatus>
                {item?.description ? (
                    <Tag
                        lineHeight={item.status === t('orders.priceOverrideHistoryStatus.canceledBy') && '1.5'}
                        canceledBy={true}
                    >
                        {item.description}
                    </Tag>
                ) : null}

                {item?.levelDescritpion ? (
                    <Tag>{item.levelDescritpion}</Tag>
                ) : null}
            </HistoryContent>
        );
    };

    return (
        <CollapsableCard
            id="orders-priceOverrideHistory-priceOverrideHistory-history"
            title={t('orders.priceOverrideHistoryStatus.history')}
            onClick={() => setExpand(!expand)}
            isExpanded={expand}
            padding={'1rem'}
            margin={
                winWidth <= sizes.mobileDevice &&
                orderBasicDetails?.currentOrderType === 'KE'
                    ? '10px 0px 0px 0px'
                    : '10px 0px 90px 0px'
            }
        >
            {expand ? (
                <>
                    <Demo className="timeline-demo">
                        <Timeline
                            value={eventsComplete}
                            opposite={(item) => (
                                <>
                                    <DateContainer>
                                        {item.date}
                                        {item?.time ? (
                                            <Character>|</Character>
                                        ) : null}
                                        <TimeContainer>
                                            {item.time}{' '}
                                            {item?.timeZone ? (
                                                <> {item.timeZone}</>
                                            ) : null}
                                        </TimeContainer>
                                    </DateContainer>
                                </>
                            )}
                            marker={customizedMarker}
                            content={customizedContent}
                        />
                    </Demo>
                </>
            ) : null}
        </CollapsableCard>
    );
};

export default PriceOverrideHistory;
